import { auth } from "./firebase";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const VISIT_DAY_KEY = "ugc_visit_day_utc";
const PENDING_STATS_KEY = "ugc_pending_stats_v1";

const DEBUG = false;

// Simple cache keys
const CATALOG_CACHE_KEY = DEBUG ? Math.random().toString(36).slice(2) : "ugc_catalog_cache_release_v1";
const RULESET_CACHE_PREFIX = DEBUG ? Math.random().toString(36).slice(2) : "ugc_ruleset_cache_release_v1";
const VISIT_KEY = "ugc_daily_visit"; 
const CALC_KEY = "ugc_last_calculation";

function utcDayKey() {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function readJson(key, fallback) {
  try {
    const s = localStorage.getItem(key);
    return s ? JSON.parse(s) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage failures
  }
}

const post = async (endpoint, payload, { beacon = false } = {}) => {
  try {
    const url = `${API_BASE}/${endpoint}`;

    if (beacon && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }

    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // never break UI for analytics
  }
};

const get = async (endpoint) => {
  const res = await fetch(`${API_BASE}/${endpoint}`, { method: "GET" });
  if (!res.ok) throw new Error(`GET ${endpoint} failed`);
  return res.json();
};

const postJson = async (endpoint, payload) => {
  const res = await fetch(`${API_BASE}/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`POST ${endpoint} failed`);
  return res.json();
};

export const trackVisit = () => {
  const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
  const now = Date.now();
  const lastVisit = Number(localStorage.getItem(VISIT_KEY)) || 0;

  if (lastVisit && now - lastVisit < FOUR_HOURS_MS) return;

  localStorage.setItem(VISIT_KEY, String(now));
  return post("trackStats", { type: "visit" });
};

export const trackCalculation = () => {
  const now = Date.now();
  const last = Number(localStorage.getItem(CALC_KEY)) || 0;

  if (now - last < 5000) return;

  localStorage.setItem(CALC_KEY, String(now));
  return post("trackStats", { type: "calculation" });
};

export const getRuleset = async (universityId, degreeType) => {
  const uni = String(universityId);
  const deg = String(degreeType).toLowerCase();
  const cacheKey = `${RULESET_CACHE_PREFIX}${uni}:${deg}`;

  const cached = readJson(cacheKey, null);
  const now = Date.now();

  // cache for 24 hours (or longer if your rulesets rarely change)
  if (cached && cached.expiresAt > now && cached.data) {
    return cached.data;
  }

  const qs = new URLSearchParams({ universityId: uni, degreeType: deg });
  const res = await fetch(`${API_BASE}/ruleset?${qs.toString()}`, { method: "GET" });
  if (!res.ok) throw new Error("Failed to load ruleset");

  const data = await res.json();
  writeJson(cacheKey, { data, expiresAt: now + 24 * 60 * 60 * 1000 });
  return data;
};

export const calculate = async ({ universityId, degreeType, inputs }) =>
  postJson("calculate", { universityId, degreeType, inputs });

export const analyse = async ({ universityId, degreeType, inputs, targetGrade }) =>
  postJsonAuthed("analyse", { universityId, degreeType, inputs, targetGrade });

export const getStats = async () => get("getStats");

export const getCatalog = async () => {
  const cached = readJson(CATALOG_CACHE_KEY, null);
  const now = Date.now();

  // cache for 24 hours
  if (cached && cached.expiresAt > now && cached.data) {
    return cached.data;
  }

  const res = await fetch(`${API_BASE}/catalog`, { method: "GET" });
  if (!res.ok) throw new Error("GET catalog failed");

  const data = await res.json();
  writeJson(CATALOG_CACHE_KEY, { data, expiresAt: now + 24 * 60 * 60 * 1000 });
  return data;
};

export const markDailyVisit = () => {
  const today = utcDayKey();
  const last = localStorage.getItem(VISIT_DAY_KEY);
  if (last === today) return false;

  localStorage.setItem(VISIT_DAY_KEY, today);

  const pending = readJson(PENDING_STATS_KEY, { visit: 0, calculation: 0 });
  pending.visit += 1;
  writeJson(PENDING_STATS_KEY, pending);
  return true;
};

export const markCalculation = () => {
  const pending = readJson(PENDING_STATS_KEY, { visit: 0, calculation: 0 });
  pending.calculation += 1;
  writeJson(PENDING_STATS_KEY, pending);
};

export const flushPendingStats = () => {
  const pending = readJson(PENDING_STATS_KEY, { visit: 0, calculation: 0 });
  if (!pending.visit && !pending.calculation) return;

  // Send once, best-effort
  post("trackStats", pending, { beacon: true });

  // Clear pending
  writeJson(PENDING_STATS_KEY, { visit: 0, calculation: 0 });
};


const postJsonWithHeaders = async (endpoint, payload, headers = {}) => {
  const res = await fetch(`${API_BASE}/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(payload ?? {}),
  });

  // Try to parse JSON either way (for better errors)
  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const msg = data?.error || data?.message || `POST ${endpoint} failed`;
    throw new Error(msg);
  }

  return data;
};

export const createCheckoutSession = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in to upgrade.");

  const idToken = await user.getIdToken();

  // If you made your function accept payload (e.g. priceId), pass it here.
  // If your function uses STRIPE_PRICE_ID from env, payload can be empty.
  const data = await postJsonWithHeaders(
    "createCheckoutSession",
    {},
    { Authorization: `Bearer ${idToken}` }
  );

  // Expecting { url } or { sessionUrl }
  const url = data?.url || data?.sessionUrl;
  if (!url) throw new Error("Checkout session URL missing from response.");

  return { url };
};

const postJsonAuthed = async (endpoint, payload, { retryOnForbidden = true } = {}) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Please sign in.");

  const doReq = async (forceRefresh) => {
    const idToken = await user.getIdToken(Boolean(forceRefresh));

    const res = await fetch(`${API_BASE}/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(payload ?? {}),
    });

    const data = await res.json().catch(() => null);

    if (!res.ok) {
      const msg = data?.error || data?.message || `POST ${endpoint} failed`;
      const err = new Error(msg);
      err.status = res.status;
      throw err;
    }

    return data;
  };

  try {
    return await doReq(false);
  } catch (e) {
    if (retryOnForbidden && (e?.status === 403 || /pro required/i.test(e?.message || ""))) {
      return await doReq(true);
    }
    throw e;
  }
};

export const exportCsv = async ({ universityId, degreeType, inputs, targetGrade }) =>
  postJsonAuthed("exportCsv", { universityId, degreeType, inputs, targetGrade });

export const importCsv = async ({ csvText }) =>
  postJsonAuthed("importCsv", { csvText });
